import { useCallback } from 'react';
import {
  ApiUnauthorizedError,
  ApiForbiddenError,
  ApiServerError,
  ApiNetworkError,
} from '@gs/api-client';
import { useToast } from '../components/Toast';

/** Fenêtre de dédup : un même message d'erreur = un seul toast (poll/endpoint en échec). */
const ERROR_TOAST_DEDUP_MS = 5_000;
const recentErrorToasts = new Map<string, number>();

function shouldShowDedupedToast(message: string, variant: 'warning' | 'error'): boolean {
  const key = `${variant}:${message}`;
  const now = Date.now();
  const last = recentErrorToasts.get(key);
  if (last != null && now - last < ERROR_TOAST_DEDUP_MS) return false;
  recentErrorToasts.set(key, now);
  return true;
}

/** Test-only — réinitialise la fenêtre de dédup entre les tests. */
export function resetErrorToastDedupForTests(): void {
  recentErrorToasts.clear();
}

/**
 * Retourne un handler `(err, kind) => void` à passer à createGsQueryClient({ onError }).
 * Traduit les erreurs typées en toasts adaptés :
 *   - 401 → silent (AUTH-01 gère déjà le silent re-auth via apiFetch retry)
 *   - 403 → toast "Accès refusé"
 *   - 5xx → toast "Erreur serveur, réessayez"
 *   - Network → toast "Problème de connexion"
 *
 * Dédup transverse : un message identique n'apparaît qu'une fois par fenêtre courte
 * (évite la pluie de toasts quand un poll ou une query échoue en boucle).
 *
 * À utiliser à l'intérieur d'un `ToastProvider`. Typiquement consommé par un
 * `QueryProvider` qui crée le QueryClient avec le handler injecté.
 */
export function useGsQueryToastHandler(): (err: Error, kind: 'query' | 'mutation') => void {
  const { toast } = useToast();

  return useCallback(
    (err: Error, _kind: 'query' | 'mutation') => {
      // 401 : silent — le retry automatique d'apiFetch + le scheduler MSAL gèrent
      if (err instanceof ApiUnauthorizedError) return;

      if (err instanceof ApiForbiddenError) {
        const message = 'Accès refusé';
        if (shouldShowDedupedToast(message, 'warning')) toast(message, 'warning');
        return;
      }

      if (err instanceof ApiNetworkError) {
        const message = 'Problème de connexion — vérifiez votre réseau';
        if (shouldShowDedupedToast(message, 'error')) toast(message, 'error');
        return;
      }

      if (err instanceof ApiServerError) {
        if (err.status >= 500) {
          const message = 'Erreur serveur — réessayez dans un instant';
          if (shouldShowDedupedToast(message, 'error')) toast(message, 'error');
        }
        return;
      }
      // Erreur inconnue : ne rien toast (probablement déjà gérée localement)
    },
    [toast],
  );
}
